import React, { useCallback, useEffect, useState } from 'react';
import { X, Mail } from 'lucide-react';
import type { WorkspaceRole } from '../../../types';

interface Props {
  onClose: () => void;
  busy: boolean;
  onSubmit: (email: string, role: WorkspaceRole) => Promise<{ ok: true } | { ok: false; code: string | null; message: string }>;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const errorMap: Record<string, string> = {
  email_already_user: 'That email is already a user in another workspace.',
  already_invited: 'An open invite already exists for this email — revoke it first.',
  invite_limit_reached: 'Open invite limit (10) reached. Revoke an invite first.',
  invalid_role: 'Invalid role.',
  admin_required: 'Admin permissions required.',
};

const InviteMemberModal: React.FC<Props> = ({ onClose, busy, onSubmit }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('member');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);

  useEffect(() => {
    setFieldError(null);
  }, [email, role]);

  const handleClose = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setFieldError('Enter a valid email address.');
      return;
    }
    const result = await onSubmit(trimmed, role);
    if (result.ok) {
      onClose();
      return;
    }
    const msg = (result.code && errorMap[result.code]) || result.message;
    if (result.code === 'email_already_user' || result.code === 'already_invited') {
      setFieldError(msg);
    } else {
      setBannerError(msg);
    }
  };

  return (
    <div className="fixed top-16 left-0 right-0 bottom-0 lg:left-56 z-50 overflow-y-auto flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" onClick={busy ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-slate-800 border border-slate-600/50 shadow-xl">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-purple-500/20 rounded-lg">
              <Mail className="w-4 h-4 text-purple-300" />
            </div>
            <h2 className="text-base font-semibold text-white">Invite a member</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1 text-gray-400 hover:text-white rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          {bannerError && (
            <div className="px-3 py-2 rounded-lg bg-red-500/20 border border-red-400/40 text-red-100 text-sm">
              {bannerError}
            </div>
          )}

          <div>
            <label htmlFor="invite-email" className="block text-xs font-medium text-gray-400 mb-1">
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              disabled={busy}
              placeholder="teammate@example.com"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {fieldError && <p className="mt-1 text-xs text-red-300">{fieldError}</p>}
          </div>

          <div>
            <label htmlFor="invite-role" className="block text-xs font-medium text-gray-400 mb-1">
              Role
            </label>
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as WorkspaceRole)}
              disabled={busy}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="member" className="bg-slate-800">Member</option>
              <option value="admin" className="bg-slate-800">Admin</option>
            </select>
            <p className="mt-1 text-xs text-gray-400">
              Admins can manage members and workspace settings. Invite is valid for 7 days.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-700/50">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3 py-2 text-sm text-gray-300 hover:text-white rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
            >
              {busy ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteMemberModal;
